import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { themes } from '../theme';

const ThemeContext = createContext();

/** Avoid dull UI: Number('')/Number(null) are 0 — old code clamped that to min brightness (~70%). */
function readStoredPercent(key, fallback, minValid, maxValid) {
  const raw = localStorage.getItem(key);
  if (raw == null || String(raw).trim() === '') return fallback;
  const v = Number(raw);
  if (!Number.isFinite(v) || v < minValid || v > maxValid) return fallback;
  return Math.round(v);
}

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

const MODES = ['light', 'medium', 'dark', 'ocean', 'rose', 'graphite'];

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('erp-theme-mode');
    return saved && themes[saved] ? saved : 'light';
  });

  const [themeBrightness, setThemeBrightness] = useState(() =>
    readStoredPercent('erp-theme-brightness', 100, 70, 130)
  );

  const [themeContrast, setThemeContrast] = useState(() =>
    readStoredPercent('erp-theme-contrast', 100, 85, 120)
  );

  const [uiFontScale, setUiFontScale] = useState(() =>
    readStoredPercent('erp-theme-font-scale', 100, 90, 115)
  );

  useEffect(() => {
    if (!themes[themeMode]) {
      setThemeMode('light');
      return;
    }
    localStorage.setItem('erp-theme-mode', themeMode);
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem('erp-theme-brightness', String(themeBrightness));
  }, [themeBrightness]);

  useEffect(() => {
    localStorage.setItem('erp-theme-contrast', String(themeContrast));
  }, [themeContrast]);

  useEffect(() => {
    localStorage.setItem('erp-theme-font-scale', String(uiFontScale));
  }, [uiFontScale]);

  const base = themes[themeMode] || themes.light;
  const theme = useMemo(
    () =>
      createTheme(base, {
        typography: {
          fontSize: Math.round(14 * (uiFontScale / 100)),
        },
      }),
    [base, uiFontScale]
  );

  useEffect(() => {
    const root = document.documentElement;
    if (themeBrightness !== 100 || themeContrast !== 100) {
      root.style.filter = `brightness(${themeBrightness}%) contrast(${themeContrast}%)`;
    } else {
      root.style.filter = '';
    }
    return () => {
      root.style.filter = '';
    };
  }, [themeBrightness, themeContrast]);

  const resetAppearance = () => {
    setThemeMode('light');
    setThemeBrightness(100);
    setThemeContrast(100);
    setUiFontScale(100);
  };

  const value = {
    themeMode,
    setThemeMode,
    themeBrightness,
    setThemeBrightness,
    themeContrast,
    setThemeContrast,
    uiFontScale,
    setUiFontScale,
    resetAppearance,
    toggleTheme: () => {
      const currentIndex = MODES.indexOf(themeMode);
      const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % MODES.length;
      setThemeMode(MODES[nextIndex]);
    },
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <Box className="erp-theme-root" sx={{ minHeight: '100vh', width: '100%' }}>
          {children}
        </Box>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
